import React, { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftSnackbar from "components/SoftSnackbar";
import SoftTypography from "components/SoftTypography";
import { apiCall } from "api";
import { API_URL } from "config";

const redirectFields = [
  {
    key: "internal_vendor_complete_redirect_url",
    label: "Complete Redirect",
    copyLabel: "Complete URL",
    status: 1,
    fallback: "/survey-response?status=1&pid=[%pid%]",
  },
  {
    key: "internal_vendor_terminate_redirect_url",
    label: "Terminate Redirect",
    copyLabel: "Terminate URL",
    status: 2,
    fallback: "/survey-response?status=2&pid=[%pid%]",
  },
  {
    key: "internal_vendor_quota_full_redirect_url",
    label: "Quota Full Redirect",
    copyLabel: "Quota Full URL",
    status: 3,
    fallback: "/survey-response?status=3&pid=[%pid%]",
  },
  {
    key: "internal_vendor_security_terminate_redirect_url",
    label: "Security Terminate Redirect",
    copyLabel: "Security Terminate URL",
    status: 4,
    fallback: "/survey-response?status=4&pid=[%pid%]",
  },
];

const defaultForm = redirectFields.reduce(
  (values, field) => ({ ...values, [field.key]: field.fallback }),
  {}
);

const fallbackBackendUrl = API_URL.replace(/\/api\/?$/i, "");

const resolveRedirectUrl = (backendUrl, value) => {
  const redirectUrl = String(value || "").trim();
  if (!redirectUrl || /^https?:\/\//i.test(redirectUrl)) return redirectUrl;

  return `${String(backendUrl || "").replace(/\/+$/, "")}/${redirectUrl.replace(/^\/+/, "")}`;
};

function InternalVendorRedirects() {
  const [form, setForm] = useState(defaultForm);
  const [backendUrl, setBackendUrl] = useState(fallbackBackendUrl);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });

  const showMessage = (message, color = "success") => {
    setSnackbar({ open: true, message, color });
  };

  const fetchRedirects = async () => {
    setLoading(true);
    try {
      const response = await apiCall("/global-redirects");
      const data = response?.data || {};
      const resolvedBackendUrl = data.backend_url || fallbackBackendUrl;
      setBackendUrl(resolvedBackendUrl);
      setCanEdit(Boolean(data.can_edit));
      setForm(
        redirectFields.reduce(
          (values, field) => ({
            ...values,
            [field.key]: resolveRedirectUrl(
              resolvedBackendUrl,
              data[field.key] || field.fallback
            ),
          }),
          {}
        )
      );
    } catch (error) {
      showMessage(error.message || "Failed to load internal vendor redirects", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRedirects();
  }, []);

  const updateField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const copyToClipboard = async (value, label) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = value;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      showMessage(`${label} copied`);
    } catch {
      showMessage(`Failed to copy ${label.toLowerCase()}`, "error");
    }
  };

  const handleCopyAll = () => {
    const redirectText = redirectFields
      .map(
        (field) =>
          `${field.copyLabel}: ${resolveRedirectUrl(
            backendUrl,
            form[field.key] || field.fallback
          )}`
      )
      .join("\n");

    copyToClipboard(redirectText, "All internal vendor redirects");
  };

  const handleSave = async () => {
    if (!canEdit) return;

    setSaving(true);
    try {
      const response = await apiCall("/global-redirects", "PUT", form);
      const data = response?.data || {};
      const resolvedBackendUrl = data.backend_url || backendUrl;
      setBackendUrl(resolvedBackendUrl);
      setCanEdit(Boolean(data.can_edit));
      setForm(
        redirectFields.reduce(
          (values, field) => ({
            ...values,
            [field.key]: resolveRedirectUrl(
              resolvedBackendUrl,
              data[field.key] || field.fallback
            ),
          }),
          {}
        )
      );
      showMessage("Internal vendor redirects updated successfully");
    } catch (error) {
      showMessage(error.message || "Failed to update internal vendor redirects", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={2}>
        <Card>
          <SoftBox p={3}>
            <SoftBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <SoftBox>
                <SoftTypography variant="h5" fontWeight="bold">
                  Internal Vendor Redirects
                </SoftTypography>
                {!loading && !canEdit && (
                  <SoftTypography variant="caption" color="text">
                    Read only
                  </SoftTypography>
                )}
              </SoftBox>
              <SoftBox display="flex" alignItems="center" gap={1}>
                <SoftButton
                  color="dark"
                  variant="outlined"
                  size="small"
                  onClick={handleCopyAll}
                  disabled={loading}
                >
                  <Icon sx={{ mr: 0.5 }}>content_copy</Icon>
                  Copy All
                </SoftButton>
                {canEdit && (
                  <SoftButton
                    color="info"
                    size="small"
                    onClick={handleSave}
                    disabled={loading || saving}
                  >
                    <Icon sx={{ mr: 0.5 }}>save</Icon>
                    {saving ? "Saving..." : "Save"}
                  </SoftButton>
                )}
              </SoftBox>
            </SoftBox>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Backend URL
                </SoftTypography>
                <SoftBox display="flex" alignItems="center" gap={1}>
                  <SoftBox flex={1} minWidth={0}>
                    <SoftInput
                      value={backendUrl}
                      inputProps={{ readOnly: true }}
                      disabled={loading}
                    />
                  </SoftBox>
                  <Tooltip title="Copy backend URL">
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Copy backend URL"
                        onClick={() => copyToClipboard(backendUrl, "Backend URL")}
                        disabled={loading || !backendUrl}
                      >
                        <Icon fontSize="small">content_copy</Icon>
                      </IconButton>
                    </span>
                  </Tooltip>
                </SoftBox>
              </Grid>

              {redirectFields.map((field) => (
                <Grid item xs={12} key={field.key}>
                  <SoftTypography variant="caption" fontWeight="bold">
                    {field.label} (Status {field.status})
                  </SoftTypography>
                  <SoftBox display="flex" alignItems="center" gap={1}>
                    <SoftBox flex={1} minWidth={0}>
                      <SoftInput
                        value={form[field.key] || ""}
                        placeholder={resolveRedirectUrl(backendUrl, field.fallback)}
                        onChange={(event) => updateField(field.key, event.target.value)}
                        inputProps={{ readOnly: !canEdit }}
                        disabled={loading || saving}
                      />
                    </SoftBox>
                    <Tooltip title={`Copy ${field.label.toLowerCase()}`}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={`Copy ${field.label.toLowerCase()}`}
                          onClick={() =>
                            copyToClipboard(
                              resolveRedirectUrl(backendUrl, form[field.key]),
                              field.label
                            )
                          }
                          disabled={loading || !form[field.key]}
                        >
                          <Icon fontSize="small">content_copy</Icon>
                        </IconButton>
                      </span>
                    </Tooltip>
                  </SoftBox>
                </Grid>
              ))}
            </Grid>
          </SoftBox>
        </Card>
      </SoftBox>

      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Internal Vendor Redirects"
        content={snackbar.message}
        open={snackbar.open}
        close={() => setSnackbar((current) => ({ ...current, open: false }))}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        time={3000}
      />
    </DashboardLayout>
  );
}

export default InternalVendorRedirects;
